import { useState, useEffect, useCallback } from 'react';
import {
  Users, Plus, Edit, Trash2, Mail, ChevronDown, ChevronRight,
  Search, X, CheckCircle, Clock, XCircle, Upload
} from 'lucide-react';
import Card from '../components/common/Card';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import CreateGroupModal from '../components/features/GroupManagement/CreateGroupModal';
import EditGroupModal from '../components/features/GroupManagement/EditGroupModal';
import ImportGroupEmailsModal from '../components/features/GroupManagement/ImportGroupEmailsModal';
import {
  getGroups, deleteGroup, getGroupEmails, getGroupChildGroups,
  addEmailsToGroup, removeEmailFromGroup,
  addChildGroupsToGroup, removeChildGroupFromGroup
} from '../services/groupServiceSwitch';
import { getEmails } from '../services/emailServiceSwitch';
import './Groups.css';

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Expanded group accordion state
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [activeTab, setActiveTab] = useState('emails'); // 'emails' or 'groups'

  // Members loaded for expanded group
  const [groupEmails, setGroupEmails] = useState([]);
  const [groupChildGroups, setGroupChildGroups] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Add member search
  const [memberSearch, setMemberSearch] = useState('');
  const [availableEmails, setAvailableEmails] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [addLoading, setAddLoading] = useState(null); // id of item being added

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const [toast, setToast] = useState(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getGroups({ search: searchTerm, page: currentPage, limit: itemsPerPage });
      setGroups(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Load members when a group is expanded
  const loadGroupMembers = useCallback(async (groupId) => {
    try {
      setMembersLoading(true);
      const [emails, childGroups] = await Promise.allSettled([
        getGroupEmails(groupId),
        getGroupChildGroups(groupId)
      ]);
      setGroupEmails(emails.status === 'fulfilled' ? emails.value : []);
      setGroupChildGroups(childGroups.status === 'fulfilled' ? childGroups.value : []);
    } catch (err) {
      showToast('Failed to load group members', 'error');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  // Load available items for the add-member picker
  const loadAvailableItems = useCallback(async (groupId, tab) => {
    try {
      if (tab === 'emails') {
        const response = await getEmails({ page: 1, limit: 750 });
        setAvailableEmails(response.data || []);
      } else {
        const response = await getGroups({ page: 1, limit: 200 });
        // Filter out the current group and any groups already added
        setAvailableGroups((response.data || []).filter(g => g.id !== groupId));
      }
    } catch {
      // Silently fail — picker will just be empty
    }
  }, []);

  const handleToggleExpand = async (group) => {
    if (expandedGroupId === group.id) {
      setExpandedGroupId(null);
      setGroupEmails([]);
      setGroupChildGroups([]);
      setMemberSearch('');
      return;
    }
    setExpandedGroupId(group.id);
    setActiveTab('emails');
    setMemberSearch('');
    await Promise.all([
      loadGroupMembers(group.id),
      loadAvailableItems(group.id, 'emails')
    ]);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setMemberSearch('');
    await loadAvailableItems(expandedGroupId, tab);
  };

  // Add email to group
  const handleAddEmail = async (emailId) => {
    try {
      setAddLoading(emailId);
      await addEmailsToGroup(expandedGroupId, [emailId]);
      await loadGroupMembers(expandedGroupId);
      fetchGroups(); // refresh counts
      showToast('Email added to group', 'success');
    } catch {
      showToast('Failed to add email', 'error');
    } finally {
      setAddLoading(null);
    }
  };

  // Remove email from group
  const handleRemoveEmail = async (emailId) => {
    try {
      await removeEmailFromGroup(expandedGroupId, emailId);
      await loadGroupMembers(expandedGroupId);
      fetchGroups();
      showToast('Email removed from group', 'success');
    } catch {
      showToast('Failed to remove email', 'error');
    }
  };

  // Add child group
  const handleAddChildGroup = async (childGroupId) => {
    try {
      setAddLoading(childGroupId);
      await addChildGroupsToGroup(expandedGroupId, [childGroupId]);
      await loadGroupMembers(expandedGroupId);
      fetchGroups();
      showToast('Group added as member', 'success');
    } catch {
      showToast('Failed to add group', 'error');
    } finally {
      setAddLoading(null);
    }
  };

  // Remove child group
  const handleRemoveChildGroup = async (childGroupId) => {
    try {
      await removeChildGroupFromGroup(expandedGroupId, childGroupId);
      await loadGroupMembers(expandedGroupId);
      fetchGroups();
      showToast('Group removed', 'success');
    } catch {
      showToast('Failed to remove group', 'error');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await deleteGroup(group.id);
      if (expandedGroupId === group.id) setExpandedGroupId(null);
      showToast('Group deleted', 'success');
      fetchGroups();
    } catch (err) {
      showToast(err.message || 'Failed to delete group', 'error');
    }
  };

  const handleSearch = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    setExpandedGroupId(null);
  };

  const showToast = (message, type = 'success') => setToast({ message, type });

  const handleSuccess = (message) => {
    showToast(message, 'success');
    fetchGroups();
  };

  // Filter available items by member search
  const filteredAvailableEmails = availableEmails.filter(e => {
    const alreadyAdded = groupEmails.some(ge => ge.id === e.id);
    if (alreadyAdded) return false;
    if (!memberSearch) return true;
    return e.email.toLowerCase().includes(memberSearch.toLowerCase());
  });

  const filteredAvailableGroups = availableGroups.filter(g => {
    const alreadyAdded = groupChildGroups.some(cg => cg.id === g.id);
    if (alreadyAdded) return false;
    if (g.id === expandedGroupId) return false;
    if (!memberSearch) return true;
    return g.name.toLowerCase().includes(memberSearch.toLowerCase());
  });

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckCircle size={14} className="status-icon status-verified" />;
      case 'failed': return <XCircle size={14} className="status-icon status-failed" />;
      default: return <Clock size={14} className="status-icon status-pending" />;
    }
  };

  return (
    <div className="groups-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <Users size={32} />
              Distribution Groups
            </h1>
            <p className="page-subtitle">Manage email distribution groups and nested group memberships</p>
          </div>
          <Button variant="primary" onClick={() => setShowCreateModal(true)} icon={<Plus size={20} />}>
            Create Group
          </Button>
        </div>
      </div>

      <Card className="groups-card">
        <div className="groups-controls">
          <SearchBar value={searchTerm} onChange={handleSearch} placeholder="Search groups by name or description..." />
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={() => window.location.reload()} />}

        {!loading && !error && (
          <>
            <div className="group-accordion-list">
              {groups.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} className="empty-icon" />
                  <p>No groups found</p>
                </div>
              ) : (
                groups.map(group => (
                  <div key={group.id} className={`group-accordion ${expandedGroupId === group.id ? 'expanded' : ''}`}>
                    {/* Accordion Header */}
                    <div className="group-accordion-header" onClick={() => handleToggleExpand(group)}>
                      <div className="group-accordion-toggle">
                        {expandedGroupId === group.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </div>
                      <div className="group-accordion-info">
                        <div className="group-accordion-name">
                          <Users size={18} className="group-icon" />
                          <span>{group.name}</span>
                        </div>
                        <span className="group-accordion-desc">{group.description}</span>
                      </div>
                      <div className="group-accordion-badges">
                        <span className="badge badge-email" title="Email members">
                          <Mail size={14} />
                          {group.emailCount}
                        </span>
                        <span className="badge badge-group" title="Child groups">
                          <Users size={14} />
                          {group.childGroupCount}
                        </span>
                      </div>
                      <div className="group-accordion-actions" onClick={e => e.stopPropagation()}>
                        <button className="action-btn edit-btn" onClick={() => { setSelectedGroup(group); setShowEditModal(true); }} title="Edit group">
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete-btn" onClick={() => handleDeleteGroup(group)} title="Delete group">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Accordion Body */}
                    {expandedGroupId === group.id && (
                      <div className="group-accordion-body">
                        {/* Tabs */}
                        <div className="member-tabs">
                          <button
                            className={`member-tab ${activeTab === 'emails' ? 'active' : ''}`}
                            onClick={() => handleTabChange('emails')}
                          >
                            <Mail size={16} />
                            Emails ({groupEmails.length})
                          </button>
                          <button
                            className={`member-tab ${activeTab === 'groups' ? 'active' : ''}`}
                            onClick={() => handleTabChange('groups')}
                          >
                            <Users size={16} />
                            Groups ({groupChildGroups.length})
                          </button>
                          <button
                            className="member-tab import-csv-tab"
                            onClick={() => setShowImportModal(true)}
                            title="Import emails from CSV into this group"
                            style={{ marginLeft: 'auto' }}
                          >
                            <Upload size={16} />
                            Import CSV
                          </button>
                        </div>

                        {membersLoading ? (
                          <div className="members-loading"><LoadingSpinner /></div>
                        ) : (
                          <div className="member-content">
                            {/* Add member search */}
                            <div className="add-member-section">
                              <div className="add-member-search">
                                <Search size={16} />
                                <input
                                  type="text"
                                  placeholder={activeTab === 'emails' ? 'Search emails to add...' : 'Search groups to add...'}
                                  value={memberSearch}
                                  onChange={e => setMemberSearch(e.target.value)}
                                  className="add-member-input"
                                />
                                {memberSearch && (
                                  <button className="clear-search-btn" onClick={() => setMemberSearch('')}>
                                    <X size={14} />
                                  </button>
                                )}
                              </div>

                              {/* Available items picker */}
                              <div className="available-items-list">
                                {activeTab === 'emails' ? (
                                  filteredAvailableEmails.length === 0 ? (
                                    <div className="no-items-hint">{memberSearch ? 'No matching emails' : 'All emails added'}</div>
                                  ) : (
                                    filteredAvailableEmails.slice(0, 8).map(email => (
                                      <button
                                        key={email.id}
                                        className="available-item"
                                        onClick={() => handleAddEmail(email.id)}
                                        disabled={addLoading === email.id}
                                      >
                                        <span className="available-item-label">
                                          {getVerificationIcon(email.verificationStatus)}
                                          {email.email}
                                        </span>
                                        {addLoading === email.id ? (
                                          <span className="add-spinner" />
                                        ) : (
                                          <Plus size={14} />
                                        )}
                                      </button>
                                    ))
                                  )
                                ) : (
                                  filteredAvailableGroups.length === 0 ? (
                                    <div className="no-items-hint">{memberSearch ? 'No matching groups' : 'No groups available'}</div>
                                  ) : (
                                    filteredAvailableGroups.slice(0, 8).map(g => (
                                      <button
                                        key={g.id}
                                        className="available-item"
                                        onClick={() => handleAddChildGroup(g.id)}
                                        disabled={addLoading === g.id}
                                      >
                                        <span className="available-item-label">
                                          <Users size={14} className="child-group-icon" />
                                          @{g.name}
                                          <span className="available-item-meta">{g.emailCount} emails</span>
                                        </span>
                                        {addLoading === g.id ? (
                                          <span className="add-spinner" />
                                        ) : (
                                          <Plus size={14} />
                                        )}
                                      </button>
                                    ))
                                  )
                                )}
                                {activeTab === 'emails' && filteredAvailableEmails.length > 8 && (
                                  <div className="more-items-hint">+{filteredAvailableEmails.length - 8} more — use search to filter</div>
                                )}
                                {activeTab === 'groups' && filteredAvailableGroups.length > 8 && (
                                  <div className="more-items-hint">+{filteredAvailableGroups.length - 8} more — use search to filter</div>
                                )}
                              </div>
                            </div>

                            {/* Current members list */}
                            <div className="current-members">
                              <h4 className="members-heading">
                                {activeTab === 'emails' ? 'Current Email Members' : 'Current Child Groups'}
                              </h4>
                              {activeTab === 'emails' ? (
                                groupEmails.length === 0 ? (
                                  <p className="no-members-text">No email members yet</p>
                                ) : (
                                  <div className="member-list">
                                    {groupEmails.map(email => (
                                      <div key={email.id} className="member-item">
                                        <span className="member-item-label">
                                          {getVerificationIcon(email.verificationStatus)}
                                          {email.email}
                                        </span>
                                        <button className="remove-member-btn" onClick={() => handleRemoveEmail(email.id)} title="Remove">
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )
                              ) : (
                                groupChildGroups.length === 0 ? (
                                  <p className="no-members-text">No child groups yet</p>
                                ) : (
                                  <div className="member-list">
                                    {groupChildGroups.map(cg => (
                                      <div key={cg.id} className="member-item member-item-group">
                                        <span className="member-item-label">
                                          <Users size={14} className="child-group-icon" />
                                          @{cg.name}
                                          <span className="member-item-meta">{cg.emailCount} emails</span>
                                        </span>
                                        <button className="remove-member-btn" onClick={() => handleRemoveChildGroup(cg.id)} title="Remove">
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}
          </>
        )}
      </Card>

      <CreateGroupModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={handleSuccess} />
      <EditGroupModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedGroup(null); }}
        group={selectedGroup}
        onSuccess={handleSuccess}
      />

      <ImportGroupEmailsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={(msg) => {
          showToast(msg, 'success');
          loadGroupMembers(expandedGroupId);
          loadAvailableItems(expandedGroupId, 'emails');
          fetchGroups();
        }}
        groupId={expandedGroupId || ''}
        groupEmails={groupEmails}
        availableEmails={availableEmails}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
